import { SectionHeader } from "../../SectionHeader";
import { ModuleGrid } from "../../../layouts/ModuleGrid";
import { Card } from "../../ui/card";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { useIsPortraitMobile } from "../../../hooks/useIsPortraiteMobile";
import TiltMessage from "../../../components/feedback/TiltMessage";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { MonthYearPicker } from "../../ui/month-Year-Picker";
import { RetailManagerialMetrics } from "../components/retail-metrics";
import { RetailMultiChannel } from "../components/retail-multichannel";
import { RetailRevenueOverview } from "../components/retail-accounting-rev";
import { RetailExpenseReport } from "../components/retail-ExpenseReport";
import { RetailAOVFrequency } from "../components/retail-AOV";
import { RetailCategoryPerformance } from "../components/retail-categoryPerformance";
import { RetailGenderCategory } from "../components/retail-GenderCategory";
import { RetailShopifyTopProducts } from "../components/retail-topProducts";
import { RetailCollectionPerformance } from "../components/retail-collectionPerformance";
import { RetailSizePerformance } from "../components/retail-sizePerformance";
import { RetailColorPerformance } from "../components/retail-colorPerformance";
import { RetailSKUPerformance } from "../components/retail-skuPerformance";
import { RetailCompletePerformance } from "../components/retail-performanceTable";
import { TriangleAlert } from "lucide-react";


export function RetailPage() {
    const now = new Date();
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date(now.getFullYear(), now.getMonth()));
    const formattedDate = selectedDate ? format(selectedDate, "yyyy-MM") : "";
    const [categorySelectedDate, setCategorySelectedDate] = useState<Date | undefined>(new Date(now.getFullYear(), now.getMonth()));
    const categoryFormattedDate = categorySelectedDate ? format(categorySelectedDate, "yyyy-MM") : "";
    const showTiltMessage = useIsPortraitMobile();

    const isCurrentMonthIncomplete = useMemo(() => {
            if (!selectedDate) return false;
            const selectedMonthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
            const selectedMonthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
            return now >= selectedMonthStart && now <= selectedMonthEnd && now.getDate() < selectedMonthEnd.getDate();
        }, [selectedDate, now]);

    return (
        <div>
            <TiltMessage show={showTiltMessage} />
            <div className={showTiltMessage ? "blur-sm pointer-events-none" : ""}>
                <SectionHeader title="Retail" />
                <div className="flex flex-row items-center justify-between">
                <div className="mb-4">
                    <MonthYearPicker selected={selectedDate} onChange={setSelectedDate} />
                </div>
                {isCurrentMonthIncomplete && (
                        <div className="text-sm text-yellow-200 bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft px-3 py-2 flex flex-row items-center">
                            <TriangleAlert /> The current month is incomplete.
                        </div>
                    )}
                    </div>

                <ModuleGrid>
                    <div className="col-span-6 flex flex-col h-full">
                        <RetailManagerialMetrics selectedMonth={formattedDate} />
                    </div>
                    {/* <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                        <EcomRevenue selectedMonth={formattedDate} />
                    </div> */}
                    <div className="col-span-6 flex flex-col h-full">
                        <RetailMultiChannel />
                    </div>
                    <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                        <RetailRevenueOverview />
                    </div>
                    <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                        <RetailExpenseReport />
                    </div>
                    <div className="col-span-6 flex flex-col h-full">
                        <RetailShopifyTopProducts selectedMonth={formattedDate} />
                    </div>
                    <div className="col-span-6 flex flex-col h-full">
                        <SummaryHeader
                            title="Category Performance"
                        />
                        <Card>
                            <Tabs defaultValue="general">

                                <div className="flex justify-between items-center w-full">
                                <TabsList className="m-4">
                                    <TabsTrigger value="general">General</TabsTrigger>
                                    <TabsTrigger value="gender">Gender</TabsTrigger>
                                    <TabsTrigger value="collection">Collection</TabsTrigger>
                                    <TabsTrigger value="size">Size</TabsTrigger>
                                    <TabsTrigger value="color">Color</TabsTrigger>
                                    <TabsTrigger value="sku">SKU</TabsTrigger>
                                </TabsList>
                               <div className="pr-3"><MonthYearPicker selected={categorySelectedDate} onChange={setCategorySelectedDate} /></div>
                                </div> 
                                <TabsContent value="general">
                                    <div className="col-span-6 flex flex-col h-full">
                                        <RetailCategoryPerformance selectedMonth={categoryFormattedDate} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="gender">
                                    <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                                        <RetailGenderCategory selectedMonth={categoryFormattedDate} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="collection">
                                    <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                                        <RetailCollectionPerformance selectedMonth={categoryFormattedDate} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="size">
                                    <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                                        <RetailSizePerformance selectedMonth={categoryFormattedDate} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="color">
                                    <div className="col-span-12 sm:col-span-6 2xl:col-span-3 flex flex-col h-full">
                                        <RetailColorPerformance selectedMonth={categoryFormattedDate} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="sku">
                                    <div className="col-span-6 flex flex-col h-full">
                                        <RetailSKUPerformance selectedMonth={categoryFormattedDate} />
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </Card>
                    </div>
                    <div className="col-span-6 flex flex-col h-full">
                        <RetailCompletePerformance selectedMonth={formattedDate} />
                    </div>
                    <div className="col-span-6 flex flex-col h-full">
                        <RetailAOVFrequency />
                    </div>
                </ModuleGrid>
            </div>
        </div>
    );
}